import { normalizeTimeValue } from '@roger/ui-components';
import type {
  DiscoveryLocation,
  InfectionCovidScreeningCreate,
  InfectionLogDetailResponse,
  InfectionSite,
  InfectionStatus,
  InfectionType,
  YesNoNa,
} from '../../services/api';
import type { InfectionSourceEvidence } from '../../services/api/types.gen';
import { formatDateToISO, parseISOToLocalDate } from '../../utils/dateUtils';

// Shared by AddInfectionLogDialog (lifecycle + submit) and its section
// components. Kept out of the dialog so hydrate/submit mapping can be tested
// without rendering the full form.

export type InfectionLogFormState = {
  primaryPhysicianId: string;
  caseManagerId: string;
  reportedByUserId: string;
  dateReported: Date | undefined;
  timeReported: string;
  reportedToUserId: string;
  sourceEvidence: InfectionSourceEvidence | null;
  canResolveType: boolean;
  infectionStatus: InfectionStatus;
  dateOfOnset: Date | undefined;
  presentUponAdmission: boolean | null;
  infectionType: InfectionType | '';
  infectionTypeOther: string;
  infectionSite: InfectionSite | '';
  infectionSiteOther: string;
  discoveryLocation: DiscoveryLocation | '';
  discoveryLocationOther: string;
  isCommunicable: string;
  infectionDescription: string;
  signsSymptomsText: string;
  covidSymptoms: Record<string, boolean | null>;
  covidContact: string;
  physicianNotified: YesNoNa | '';
  physicianNotifiedPhysicianId: string;
  physicianNotifiedDate: Date | undefined;
  physicianNotifiedTime: string;
  labsOrdered: YesNoNa | '';
  labsOrderedDate: Date | undefined;
  labsOrderedTime: string;
  labsOrderedNotes: string;
  patientHospitalized: YesNoNa | '';
  patientHospitalizedDate: Date | undefined;
  patientHospitalizedTime: string;
  patientHospitalizedNotes: string;
  cultureTaken: YesNoNa | '';
  cultureTakenDate: Date | undefined;
  cultureTakenTime: string;
  cultureSource: string;
  treatmentNotes: string;
  followUpDueDate: Date | undefined;
  followUpAssigneeId: string;
};

export type UpdateInfectionLogField = <K extends keyof InfectionLogFormState>(
  key: K,
  value:
    | InfectionLogFormState[K]
    | ((prev: InfectionLogFormState[K]) => InfectionLogFormState[K])
) => void;

export function emptyInfectionLogForm(): InfectionLogFormState {
  return {
    primaryPhysicianId: '',
    caseManagerId: '',
    reportedByUserId: '',
    dateReported: undefined,
    timeReported: '',
    reportedToUserId: '',
    sourceEvidence: null,
    canResolveType: false,
    infectionStatus: 'investigating',
    dateOfOnset: undefined,
    presentUponAdmission: false,
    infectionType: '',
    infectionTypeOther: '',
    infectionSite: '',
    infectionSiteOther: '',
    discoveryLocation: '',
    discoveryLocationOther: '',
    isCommunicable: '',
    infectionDescription: '',
    signsSymptomsText: '',
    covidSymptoms: {},
    covidContact: '',
    physicianNotified: '',
    physicianNotifiedPhysicianId: '',
    physicianNotifiedDate: undefined,
    physicianNotifiedTime: '',
    labsOrdered: '',
    labsOrderedDate: undefined,
    labsOrderedTime: '',
    labsOrderedNotes: '',
    patientHospitalized: '',
    patientHospitalizedDate: undefined,
    patientHospitalizedTime: '',
    patientHospitalizedNotes: '',
    cultureTaken: '',
    cultureTakenDate: undefined,
    cultureTakenTime: '',
    cultureSource: '',
    treatmentNotes: '',
    followUpDueDate: undefined,
    followUpAssigneeId: '',
  };
}

const toISO = (d: Date | null | undefined): string | null =>
  d ? formatDateToISO(d) : null;

export function detailToInfectionLogForm(
  d: InfectionLogDetailResponse
): InfectionLogFormState {
  const screenings = d.covid_screenings ?? [];
  const latestScreening = screenings[screenings.length - 1];

  return {
    ...emptyInfectionLogForm(),
    sourceEvidence: d.source_evidence ?? null,
    canResolveType:
      Boolean(d.source_evidence) && d.infection_type === 'unknown',
    primaryPhysicianId: d.primary_physician_id ?? '',
    caseManagerId: d.case_manager_id ?? '',
    reportedByUserId: d.reported_by_user_id ?? '',
    reportedToUserId: d.reported_to_user_id ?? '',
    dateReported: d.date_reported
      ? parseISOToLocalDate(d.date_reported)
      : undefined,
    timeReported: normalizeTimeValue(d.time_reported ?? '') || '',
    infectionStatus: d.infection_status ?? 'investigating',
    dateOfOnset: d.date_of_onset
      ? parseISOToLocalDate(d.date_of_onset)
      : undefined,
    presentUponAdmission: d.present_upon_admission ?? null,
    infectionType: d.infection_type ?? '',
    infectionTypeOther: d.infection_type_other ?? '',
    infectionSite: d.infection_site ?? '',
    infectionSiteOther: d.infection_site_other ?? '',
    discoveryLocation: d.discovery_location ?? '',
    discoveryLocationOther: d.discovery_location_other ?? '',
    isCommunicable:
      d.is_communicable === true
        ? 'yes'
        : d.is_communicable === false
          ? 'no'
          : '',
    infectionDescription: d.infection_description ?? '',
    signsSymptomsText: d.signs_symptoms ?? '',
    physicianNotified: d.physician_notified ?? '',
    physicianNotifiedPhysicianId: d.physician_notified_physician_id ?? '',
    physicianNotifiedDate: d.physician_notified_date
      ? parseISOToLocalDate(d.physician_notified_date)
      : undefined,
    physicianNotifiedTime:
      normalizeTimeValue(d.physician_notified_time ?? '') || '',
    labsOrdered: d.labs_ordered ?? '',
    labsOrderedDate: d.labs_ordered_date
      ? parseISOToLocalDate(d.labs_ordered_date)
      : undefined,
    labsOrderedTime: normalizeTimeValue(d.labs_ordered_time ?? '') || '',
    labsOrderedNotes: d.labs_ordered_notes ?? '',
    patientHospitalized: d.patient_hospitalized ?? '',
    patientHospitalizedDate: d.patient_hospitalized_date
      ? parseISOToLocalDate(d.patient_hospitalized_date)
      : undefined,
    patientHospitalizedTime:
      normalizeTimeValue(d.patient_hospitalized_time ?? '') || '',
    patientHospitalizedNotes: d.patient_hospitalized_notes ?? '',
    cultureTaken: d.culture_taken ?? '',
    cultureTakenDate: d.culture_taken_date
      ? parseISOToLocalDate(d.culture_taken_date)
      : undefined,
    cultureTakenTime: normalizeTimeValue(d.culture_taken_time ?? '') || '',
    cultureSource: d.culture_source ?? '',
    treatmentNotes: d.treatment_notes ?? '',
    followUpDueDate: d.follow_up_due_date
      ? parseISOToLocalDate(d.follow_up_due_date)
      : undefined,
    followUpAssigneeId: d.follow_up_assignee_id ?? '',
    covidSymptoms: latestScreening
      ? {
          cough: latestScreening.symptom_cough ?? null,
          congestion: latestScreening.symptom_congestion ?? null,
          shortness_of_breath:
            latestScreening.symptom_shortness_of_breath ?? null,
          headache: latestScreening.symptom_headache ?? null,
          fever_chills: latestScreening.symptom_fever_chills ?? null,
          loss_taste_smell: latestScreening.symptom_loss_taste_smell ?? null,
          sore_throat: latestScreening.symptom_sore_throat ?? null,
          fatigue: latestScreening.symptom_fatigue ?? null,
          diarrhea: latestScreening.symptom_diarrhea ?? null,
          muscle_aches: latestScreening.symptom_muscle_aches ?? null,
          nausea_vomiting: latestScreening.symptom_nausea_vomiting ?? null,
          new_confusion: latestScreening.symptom_new_confusion ?? null,
        }
      : {},
    covidContact: latestScreening?.covid_contact ?? '',
  };
}

export function buildCovidScreening(
  form: InfectionLogFormState
): InfectionCovidScreeningCreate | undefined {
  if (form.infectionType !== 'covid_19') return undefined;
  return {
    screened_at: toISO(form.dateReported) ?? '',
    symptom_cough: form.covidSymptoms['cough'] ?? null,
    symptom_congestion: form.covidSymptoms['congestion'] ?? null,
    symptom_shortness_of_breath:
      form.covidSymptoms['shortness_of_breath'] ?? null,
    symptom_headache: form.covidSymptoms['headache'] ?? null,
    symptom_fever_chills: form.covidSymptoms['fever_chills'] ?? null,
    symptom_loss_taste_smell: form.covidSymptoms['loss_taste_smell'] ?? null,
    symptom_sore_throat: form.covidSymptoms['sore_throat'] ?? null,
    symptom_fatigue: form.covidSymptoms['fatigue'] ?? null,
    symptom_diarrhea: form.covidSymptoms['diarrhea'] ?? null,
    symptom_muscle_aches: form.covidSymptoms['muscle_aches'] ?? null,
    symptom_nausea_vomiting: form.covidSymptoms['nausea_vomiting'] ?? null,
    symptom_new_confusion: form.covidSymptoms['new_confusion'] ?? null,
    covid_contact: form.covidContact || null,
  };
}

/** Shared fields for create and update infection-log payloads. */
export function buildInfectionLogSharedBody(form: InfectionLogFormState) {
  const isCovid = form.infectionType === 'covid_19';
  return {
    date_reported: toISO(form.dateReported) ?? '',
    time_reported: form.timeReported || null,
    primary_physician_id: form.primaryPhysicianId || null,
    case_manager_id: form.caseManagerId || null,
    reported_by_user_id: form.reportedByUserId || null,
    reported_to_user_id: form.reportedToUserId || null,
    infection_status: form.infectionStatus,
    date_of_onset: form.presentUponAdmission ? null : toISO(form.dateOfOnset),
    present_upon_admission: form.presentUponAdmission,
    infection_site: form.infectionSite || null,
    infection_site_other:
      form.infectionSite === 'other' ? form.infectionSiteOther || null : null,
    discovery_location: form.discoveryLocation || null,
    discovery_location_other:
      form.discoveryLocation === 'other'
        ? form.discoveryLocationOther || null
        : null,
    is_communicable:
      form.isCommunicable === 'yes'
        ? true
        : form.isCommunicable === 'no'
          ? false
          : null,
    infection_description: isCovid ? null : form.infectionDescription || null,
    signs_symptoms: isCovid ? null : form.signsSymptomsText || null,
    physician_notified: form.physicianNotified || null,
    physician_notified_physician_id:
      form.physicianNotified === 'yes'
        ? form.physicianNotifiedPhysicianId || null
        : null,
    physician_notified_date:
      form.physicianNotified === 'yes'
        ? toISO(form.physicianNotifiedDate)
        : null,
    physician_notified_time:
      form.physicianNotified === 'yes'
        ? form.physicianNotifiedTime || null
        : null,
    labs_ordered: form.labsOrdered || null,
    labs_ordered_date:
      form.labsOrdered === 'yes' ? toISO(form.labsOrderedDate) : null,
    labs_ordered_time:
      form.labsOrdered === 'yes' ? form.labsOrderedTime || null : null,
    labs_ordered_notes:
      form.labsOrdered === 'yes' ? form.labsOrderedNotes || null : null,
    patient_hospitalized: form.patientHospitalized || null,
    patient_hospitalized_date:
      form.patientHospitalized === 'yes'
        ? toISO(form.patientHospitalizedDate)
        : null,
    patient_hospitalized_time:
      form.patientHospitalized === 'yes'
        ? form.patientHospitalizedTime || null
        : null,
    patient_hospitalized_notes:
      form.patientHospitalized === 'yes'
        ? form.patientHospitalizedNotes || null
        : null,
    culture_taken: form.cultureTaken || null,
    culture_taken_date:
      form.cultureTaken === 'yes' ? toISO(form.cultureTakenDate) : null,
    culture_taken_time:
      form.cultureTaken === 'yes' ? form.cultureTakenTime || null : null,
    culture_source:
      form.cultureTaken === 'yes' ? form.cultureSource || null : null,
    treatment_notes: form.treatmentNotes || null,
    follow_up_due_date: toISO(form.followUpDueDate),
    follow_up_assignee_id: form.followUpAssigneeId || null,
  };
}

export function buildInfectionLogUpdateBody(form: InfectionLogFormState) {
  const sharedBody = buildInfectionLogSharedBody(form);
  return {
    ...sharedBody,
    ...(form.canResolveType &&
    form.infectionType !== '' &&
    form.infectionType !== 'unknown'
      ? {
          infection_type: form.infectionType,
          infection_type_other:
            form.infectionType === 'other'
              ? form.infectionTypeOther || null
              : null,
        }
      : {}),
  };
}

export function buildInfectionLogCreateBody(
  form: InfectionLogFormState,
  episodeId?: string | null
) {
  return {
    ...buildInfectionLogSharedBody(form),
    // Stamp the selected episode only on create — edits keep the
    // log's own episode (the backend leaves absent fields untouched).
    episode_id: episodeId ?? null,
    // Submit is gated on infectionType; empty is form-only placeholder.
    infection_type: form.infectionType as InfectionType,
    infection_type_other:
      form.infectionType === 'other' ? form.infectionTypeOther || null : null,
    covid_screening: buildCovidScreening(form),
  };
}
